/**
 * Data Converter
 *
 * Converts CollectedData (UI format) to SystemRecInput (engine format).
 * This is a pure function with no React dependencies, making it reusable
 * across different contexts (RecommendationContext, HubPage, mapping store).
 * 
 * Note: This module depends on CollectedData type from RecommendationContext.
 * This is intentional as the converter sits at the boundary between UI and engine layers,
 * transforming UI data structures to engine input format.
 */

import type { SystemRecInput, SedbukBand, FuturePlan } from '@core/types';
import { getEfficiencyFromBand, isValidSedbukBand } from '@core/efficiency';
import { normalizeUnknown, normalizeTextField, safeParseNumber, safeParseInt } from '@core/sanitize';
import type { CollectedData } from '../context/RecommendationContext';
import { questionMappings } from './registry';

// Hub data types (imported from HubPage types)
interface HubSystemDetails {
  dhwArchitecture?: string;
  circuitType?: string;
  controlTopology?: string;
  heatSource?: string;
  gcNumber?: string;
  boilerMake?: string;
  boilerModel?: string;
  boilerOutput?: string;
  sedbukRating?: string;
  sedbukBand?: string;
  systemAge?: string;
  temperatureControls?: string;
}

interface HubWater {
  dhwSystemType?: string;
  occupants?: string;
  cylinderTypeProfile?: string;
  hardnessBand?: string;
}

interface HubEmitters {
  emitters?: string;
  lowTempCapability?: string;
  performanceSignal?: string;
  primarySizeBand?: string;
}

interface HubDHWPressure {
  mainsFlowLpm?: number | null;
  mainsStaticPressureBar?: number | null;
  mainsDynamicFlowLpm?: number | null;
  mainsDynamicPressureBar?: number | null;
  coldMainType?: string;
}

interface HubHeatDemand {
  bedrooms?: string;
  bathrooms?: string;
  dwellingForm?: string;
  fabricEra?: string;
  retrofitLevel?: string;
}

interface HubPriority {
  key: string;
  rank: number;
}

interface HubData {
  systemDetails?: HubSystemDetails;
  water?: HubWater;
  emitters?: HubEmitters;
  dhwPressure?: HubDHWPressure;
  heatDemand?: HubHeatDemand;
  priorities?: HubPriority[];
  futurePlans?: FuturePlan[];
  urgency?: string;
  disruptionTolerance?: string;
}

interface CollectedDataWithHub extends CollectedData {
  _hubData?: HubData;
}

/**
 * Infer boiler type from hub-collected DHW architecture and circuit type
 * 
 * **Display-only helper** - This function generates human-readable labels for diagnostics and UI.
 * Engine logic should NOT branch on currentBoiler; use explicit fields (heatSource, dhwArchitecture, 
 * chCircuitType) instead to avoid hidden coupling.
 * 
 * @param dhwArchitecture - DHW architecture ('combi-on-demand' or 'stored-dhw')
 * @param circuitType - Heating circuit type ('sealed' or 'open-vented')
 * @param heatSource - Optional heat source (for gas combi differentiation)
 * @returns Inferred boiler type string for display purposes
 */
export function inferBoilerTypeFromHub(
  dhwArchitecture: string | undefined,
  circuitType: string | undefined,
  heatSource?: string
): string {
  if (dhwArchitecture === 'combi-on-demand') {
    // Combi boiler - no stored DHW
    return heatSource === 'gas' ? 'gas_combi' : 'combi';
  }
  if (circuitType === 'sealed') {
    // System boiler with sealed heating circuit
    return 'system';
  }
  // Regular boiler with open-vented heating circuit
  return 'regular';
}

// Cache the radiatorSize mapping for performance
const RADIATOR_SIZE_MAPPING = questionMappings.find(m => m.questionId === 'radiatorSize');

// Retrofit value type for mapping
type RetrofitValue = 'none' | 'some' | 'good' | 'cavity' | 'solid_or_internal_or_external' | 
                     'single' | 'older_double' | 'modern_double_or_triple' | 'unknown';

function inferWallInsulationFromAgeAndUpgrade(data: CollectedData): string | undefined {
  const explicitWallInsulation = normalizeUnknown(data.retrofitWallInsulation);
  if (explicitWallInsulation && explicitWallInsulation !== 'unknown') {
    return explicitWallInsulation;
  }

  const ageBand = normalizeUnknown(data.propertyAgeBand);
  const insulationUpgradeSignal = normalizeUnknown(data.insulationLevel);
  const hasUpgradeSignal = insulationUpgradeSignal === 'average' || insulationUpgradeSignal === 'good' || insulationUpgradeSignal === 'modern';

  if (!ageBand || ageBand === 'unknown') return undefined;

  if (ageBand === 'pre_1919' || ageBand === '1919_1944') {
    return hasUpgradeSignal ? 'solid_insulated' : 'solid_uninsulated';
  }

  if (
    ageBand === '1945_1964' ||
    ageBand === '1965_1980' ||
    ageBand === '1981_2000' ||
    ageBand === '2001_2010' ||
    ageBand === '2011_plus'
  ) {
    return 'cavity';
  }

  return undefined;
}


function normalizeFuturePlans(data: CollectedData): Array<{ key: string; horizon: '0_2' | '2_5' | '5_10' | 'unknown' }> {
  const plans = Array.isArray(data.futurePlans) ? data.futurePlans : [];

  return plans
    .map((plan) => {
      if (!plan || typeof plan !== 'object') return null;
      const key = typeof plan.key === 'string' ? plan.key : '';
      if (!key) return null;

      const horizon =
        plan.horizon === '0_2' ||
        plan.horizon === '2_5' ||
        plan.horizon === '5_10' ||
        plan.horizon === 'unknown'
          ? plan.horizon
          : 'unknown';

      return { key, horizon };
    })
    .filter((plan): plan is { key: string; horizon: '0_2' | '2_5' | '5_10' | 'unknown' } => !!plan);
}

/**
 * Map UI retrofit selections to canonical engine enums
 * This provides defense-in-depth alongside the engine's normalization
 */
function mapRetrofitSelectionsToEngine(
  uiValue: string | undefined,
  field: 'loft' | 'wall' | 'windows' | 'draught'
): RetrofitValue {
  if (!uiValue) return 'unknown';

  // Simplified insulation level mapping (good/average/poor)
  if (uiValue === 'good') {
    return field === 'wall' ? 'cavity' : field === 'windows' ? 'modern_double_or_triple' : 'good';
  }
  if (uiValue === 'average') {
    return field === 'wall' ? 'cavity' : field === 'windows' ? 'older_double' : 'some';
  }
  if (uiValue === 'poor') {
    return field === 'wall' ? 'none' : field === 'windows' ? 'single' : 'none';
  }

  // Loft and draught-proofing use the same mapping
  if (field === 'loft' || field === 'draught') {
    switch (uiValue) {
      case 'none': return 'none';
      case 'basic': return 'some';
      case 'partial': return 'some';
      case 'modern': return 'good';
      case 'good': return 'good';
      case 'some': return 'some';
      case 'not_sure': return 'unknown';
      case 'unknown': return 'unknown';
      default: return 'unknown';
    }
  }

  // Wall insulation mapping
  if (field === 'wall') {
    switch (uiValue) {
      case 'none': return 'none';
      case 'cavity': return 'cavity';
      case 'solid_insulated': return 'solid_or_internal_or_external';
      case 'solid_or_internal_or_external': return 'solid_or_internal_or_external';
      case 'solid_uninsulated': return 'none';
      case 'not_sure': return 'unknown';
      case 'unknown': return 'unknown';
      default: return 'unknown';
    }
  }

  // Windows mapping
  if (field === 'windows') {
    switch (uiValue) {
      case 'single': return 'single';
      case 'double': return 'older_double';
      case 'older_double': return 'older_double';
      case 'modern': return 'modern_double_or_triple';
      case 'modern_double_or_triple': return 'modern_double_or_triple';
      case 'triple': return 'modern_double_or_triple';
      case 'not_sure': return 'unknown';
      case 'unknown': return 'unknown';
      default: return 'unknown';
    }
  }

  return 'unknown';
}

/**
 * Convert CollectedData (UI format) to SystemRecInput (engine format)
 * 
 * This is a pure function that can be called from any context without React dependencies.
 * 
 * **Hub Data Bridge**: When _hubData is present, it takes precedence over legacy flat fields.
 * This ensures collected assessment data drives normalization instead of stale baseline fields.
 */
export function convertToSystemRecInput(data: CollectedData): SystemRecInput {
  // Check if hub data is present (from HubPage)
  const dataWithHub = data as CollectedDataWithHub;
  
  // If hub data exists with required sections, use it as the primary source
  // Required: systemDetails + water (core system identification)
  // Optional: dhwPressure, emitters (merged if present, warnings if missing)
  if (dataWithHub._hubData?.systemDetails && dataWithHub._hubData?.water) {
    // Debug: log missing optional sections in development
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      if (!dataWithHub._hubData.dhwPressure) {
        console.warn('[converter] Hub data missing dhwPressure section');
      }
      if (!dataWithHub._hubData.emitters) {
        console.warn('[converter] Hub data missing emitters section');
      }
    }
    return convertHubDataToSystemRecInput(data, dataWithHub._hubData);
  }
  
  // Otherwise, fall back to legacy conversion
  return convertLegacyDataToSystemRecInput(data);
}

/**
 * Convert hub-collected data to SystemRecInput
 * This path is used when assessment data was collected via HubPage
 */
function convertHubDataToSystemRecInput(data: CollectedData, hubData: HubData): SystemRecInput {
  const inferredWallInsulation = inferWallInsulationFromAgeAndUpgrade(data);

  // Parse numeric fields from hub data
  const bedrooms = Number.parseInt(hubData.heatDemand?.bedrooms || data.bedrooms || '0', 10);
  const bathrooms = Number.parseInt(hubData.heatDemand?.bathrooms || data.bathrooms || '0', 10);
  const occupancy = Number.parseInt(hubData.water?.occupants || data.occupancyCount || '0', 10);
  
  // Infer current boiler type from hub data using shared utility
  const heatSource = hubData.systemDetails?.heatSource;
  const inferredCurrentBoiler = inferBoilerTypeFromHub(
    hubData.systemDetails?.dhwArchitecture,
    hubData.systemDetails?.circuitType,
    heatSource
  );

  // Map DHW system type to currentWater
  const dhwSystemType = hubData.water?.dhwSystemType || 'unknown';
  const currentWater = dhwSystemType === 'combi-on-demand' ? 'on_demand'
    : dhwSystemType === 'vented-cylinder' ? 'open_vented'
    : dhwSystemType === 'unvented-cylinder' ? 'unvented'
    : dhwSystemType === 'thermal-store' ? 'thermal_store'
    : 'open_vented';

  // Gas availability from hub heat source
  const gasAvailable = heatSource === 'gas' || heatSource === 'lpg';

  // Insulation level from hub retrofit data
  const retrofitLevel = hubData.heatDemand?.retrofitLevel || 'unknown';
  const insulationLevel = retrofitLevel === 'full' ? 'high'
    : retrofitLevel === 'partial' ? 'medium'
    : retrofitLevel === 'none' ? 'poor'
    : undefined;

  // Parse boiler output from hub system details
  const boilerOutputStr = hubData.systemDetails?.boilerOutput;
  const boilerOutputKw = boilerOutputStr && boilerOutputStr !== 'unknown'
    ? parseBoilerOutputValue(boilerOutputStr)
    : undefined;

  // Build efficiency object from hub SEDBUK data
  const efficiency: SystemRecInput['currentSystemEfficiency'] = {};
  let hasEfficiencyData = false;
  
  if (hubData.systemDetails?.gcNumber) {
    efficiency.gcNumber = hubData.systemDetails.gcNumber;
    hasEfficiencyData = true;
  }
  if (hubData.systemDetails?.boilerMake) {
    efficiency.boilerMake = hubData.systemDetails.boilerMake;
    hasEfficiencyData = true;
  }
  if (hubData.systemDetails?.boilerModel) {
    efficiency.boilerModel = hubData.systemDetails.boilerModel;
    hasEfficiencyData = true;
  }
  if (boilerOutputKw) {
    efficiency.boilerOutputKw = boilerOutputKw;
    hasEfficiencyData = true;
  }
  if (hubData.systemDetails?.sedbukRating) {
    const rating = Number.parseFloat(hubData.systemDetails.sedbukRating);
    if (Number.isFinite(rating) && rating > 0) {
      efficiency.sedbukSeasonal = rating > 1 ? rating / 100 : rating;
      efficiency.source = 'SEDBUK';
      hasEfficiencyData = true;
    }
  }
  if (hubData.systemDetails?.sedbukBand && hubData.systemDetails.sedbukBand !== 'unknown') {
    efficiency.sedbukBand = hubData.systemDetails.sedbukBand.toUpperCase() as 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
    if (!efficiency.source) efficiency.source = 'SEDBUK';
    hasEfficiencyData = true;
  }
  if (!efficiency.source && hasEfficiencyData) {
    efficiency.source = 'ASSUMED';
  }

  // Build emitters object from hub data
  const emitterType = hubData.emitters?.emitters || 'unknown';
  const emittersInput: SystemRecInput['emitters'] = {
    type: emitterType === 'ufh' ? 'underfloor'
      : emitterType === 'mixed' ? 'mixed'
      : emitterType === 'radiators' ? 'radiators'
      : 'unknown',
    lowTempSuitability: hubData.emitters?.lowTempCapability === 'yes' ? 'likely_ok'
      : hubData.emitters?.lowTempCapability === 'no' ? 'likely_needs_upgrade'
      : 'unknown',
    roomsStruggleToHeat: hubData.emitters?.performanceSignal === 'some-struggle' || hubData.emitters?.performanceSignal === 'many-struggle' ? 'yes'
      : hubData.emitters?.performanceSignal === 'all-heat-well' ? 'no'
      : 'unknown',
    pipeBore: (hubData.emitters?.primarySizeBand && hubData.emitters.primarySizeBand !== 'unknown') ? hubData.emitters.primarySizeBand as '15mm' | '22mm' : 'unknown',
  };

  // Map water hardness
  const waterHardness = hubData.water?.hardnessBand !== 'unknown' ? hubData.water.hardnessBand as SystemRecInput['waterHardness'] : undefined;

  // Map system age to years
  const systemAgeStr = hubData.systemDetails?.systemAge;
  let existingSystemAge: number | undefined;
  if (systemAgeStr) {
    const ageNum = Number.parseInt(systemAgeStr, 10);
    if (Number.isFinite(ageNum)) existingSystemAge = ageNum;
  }

  // Map Mixergy cylinder type to hotWater variant
  let hotWater: SystemRecInput['hotWater'] | undefined;
  const cylinderType = hubData.water?.cylinderTypeProfile;
  if (cylinderType === 'mixergy-vented' || cylinderType === 'mixergy-unvented') {
    hotWater = {
      strategy: cylinderType === 'mixergy-vented' ? 'open_vented' : 'unvented',
      variant: 'mixergy',
    };
  }

  // Map property type from dwelling form
  const dwellingForm = hubData.heatDemand?.dwellingForm || data.propertyType || 'unknown';
  const propertyType = dwellingForm === 'semi-detached' ? 'semi'
    : dwellingForm === 'mid-terrace' || dwellingForm === 'end-terrace' ? 'terraced'
    : dwellingForm === 'flat-top' || dwellingForm === 'flat-mid' ? 'flat'
    : dwellingForm === 'unknown' ? 'semi'
    : dwellingForm;

  // Map fabric era to property age band
  const fabricEra = hubData.heatDemand?.fabricEra || data.propertyAgeBand || 'unknown';
  const propertyAgeBand = fabricEra === 'pre-1919' ? 'pre_1919'
    : fabricEra === '1919-1944' ? '1919_1944'
    : fabricEra === '1945-1964' ? '1945_1964'
    : fabricEra === '1965-1980' ? '1965_1980'
    : fabricEra === '1981-1995' ? '1981_2000'
    : fabricEra === '1996-2006' ? '2001_2010'
    : fabricEra === '2007-2013' || fabricEra === '2013-present' ? '2011_plus'
    : 'unknown';

  return {
    propertyType,
    propertyAgeBand: propertyAgeBand as SystemRecInput['propertyAgeBand'],
    bedrooms: Number.isFinite(bedrooms) ? bedrooms : 0,
    bathrooms: Number.isFinite(bathrooms) ? bathrooms : 0,
    occupancy: Number.isFinite(occupancy) ? occupancy : 1,
    hotWaterDemand: occupancy >= 4 ? 'high' : occupancy >= 2 ? 'medium' : 'low',
    drawsPerDay: Number.isFinite(occupancy) ? Math.max(3, occupancy * 2) : 6,
    
    // Current system - derived from hub data
    currentBoiler: inferredCurrentBoiler,
    currentWater,
    
    // Explicit system architecture fields (for better diagnostics)
    heatSource: (heatSource && heatSource !== 'unknown' && heatSource !== 'heat-pump') 
      ? heatSource as SystemRecInput['heatSource'] 
      : 'unknown',
    dhwArchitecture: (hubData.systemDetails?.dhwArchitecture === 'combi-on-demand' || hubData.systemDetails?.dhwArchitecture === 'stored-dhw')
      ? hubData.systemDetails.dhwArchitecture
      : 'unknown',
    chCircuitType: (hubData.systemDetails?.circuitType === 'sealed' || hubData.systemDetails?.circuitType === 'open-vented')
      ? hubData.systemDetails.circuitType
      : 'unknown',
    // Derive chCirculation from hub emitters pumpingArrangement
    // Default to 'pumped' for open-vented gas/oil/lpg systems when unknown (95%+ are pumped)
    chCirculation: (() => {
      const pumpingArrangement = hubData.emitters?.pumpingArrangement;
      if (pumpingArrangement === 'pumped' || pumpingArrangement === 'gravity') {
        return pumpingArrangement;
      }
      // Default to 'pumped' for traditional boiler systems (gas/oil/lpg) when unknown
      // 95%+ of open-vented gas/oil/lpg systems use pumped circulation
      const isTraditionalBoilerSystem = (heatSource === 'gas' || heatSource === 'oil' || heatSource === 'lpg') 
        && hubData.systemDetails?.circuitType === 'open-vented';
      return isTraditionalBoilerSystem ? 'pumped' : 'unknown';
    })(),
    controlTopology: hubData.systemDetails?.controlTopology as SystemRecInput['controlTopology'] || undefined,

    // Electrical and pressure from hub DHW pressure data
    electricalCapacityConfidence: heatSource === 'heat-pump' ? 'likely_ok' : 'unknown',
    mainsFlowLpm: hubData.dhwPressure?.mainsFlowLpm ?? undefined,
    mainsStaticPressureBar: hubData.dhwPressure?.mainsStaticPressureBar ?? undefined,
    mainsDynamicFlowLpm: hubData.dhwPressure?.mainsDynamicFlowLpm ?? undefined,
    mainsDynamicPressureBar: hubData.dhwPressure?.mainsDynamicPressureBar ?? undefined,
    coldMainType: hubData.dhwPressure?.coldMainType !== 'unknown' ? hubData.dhwPressure?.coldMainType : undefined,

    // Plans and priorities from hub data
    expectedLengthOfStay: 'medium',
    costPriority: 'balanced',
    upgradePriorities: hubData.priorities?.map(p => ({ 
      key: p.key as SystemRecInput['upgradePriorities'][number]['key'], 
      rank: p.rank as SystemRecInput['upgradePriorities'][number]['rank']
    })) || [],
    futurePlans: hubData.futurePlans || [],
    urgency: hubData.urgency && hubData.urgency !== 'unknown' ? hubData.urgency as SystemRecInput['urgency'] : 'planning',
    disruptionTolerance: hubData.disruptionTolerance && hubData.disruptionTolerance !== 'unknown' ? hubData.disruptionTolerance as SystemRecInput['disruptionTolerance'] : 'moderate',
    futureDemand: 'stable',
    temporaryGuests: 'occasional',

    // Heat emitters
    heatEmitterType: emitterType === 'ufh' ? 'underfloor_heating' : 'standard_rads',
    emitters: emittersInput,

    // System characteristics
    baselineEnabled: true,
    gasAvailable: gasAvailable ? gasAvailable : undefined,
    insulationLevel,
    currentSystemEfficiency: hasEfficiencyData ? efficiency : undefined,
    currentFuelType: heatSource !== 'unknown' && heatSource !== 'heat-pump' && heatSource !== 'hybrid'
      ? heatSource as 'gas' | 'oil' | 'electric' | 'lpg'
      : 'gas',
    waterHardness,
    existingSystemAge,
    currentThermostatType: hubData.systemDetails?.temperatureControls !== 'unknown'
      ? hubData.systemDetails?.temperatureControls as SystemRecInput['currentThermostatType']
      : undefined,
    systemAlreadySealed: hubData.systemDetails?.circuitType === 'sealed' ? 'yes'
      : hubData.systemDetails?.circuitType === 'open-vented' ? 'no'
      : 'unknown',
    hotWater,

    // Retrofit profile from hub data
    ...(inferredWallInsulation || retrofitLevel !== 'unknown' ? {
      retrofit: {
        loftInsulation: retrofitLevel === 'full' ? 'good' : retrofitLevel === 'partial' ? 'some' : 'unknown',
        wallInsulation: (inferredWallInsulation || (retrofitLevel === 'full' ? 'cavity' : 'unknown')) as SystemRecInput['retrofit']['wallInsulation'],
        windows: retrofitLevel === 'full' ? 'modern_double_or_triple' : retrofitLevel === 'partial' ? 'older_double' : 'unknown',
        draughtProofing: retrofitLevel === 'full' ? 'good' : retrofitLevel === 'partial' ? 'some' : 'unknown',
      },
    } : {}),
    
    // Dev-only provenance marker for diagnostics
    __source: 'hub',
  };
}

/**
 * Helper to parse boiler output from string (e.g., "24 kW" → 24)
 */
function parseBoilerOutputValue(output: string): number | undefined {
  const value = output.trim().toLowerCase();
  if (!value || value === 'unknown') return undefined;
  const match = value.match(/(\d+(?:\.\d+)?)/);
  if (match) {
    const num = Number.parseFloat(match[1]);
    return Number.isFinite(num) ? num : undefined;
  }
  return undefined;
}

/**
 * Convert legacy flat fields to SystemRecInput
 * This path is used for backward compatibility with pre-hub data
 */
function convertLegacyDataToSystemRecInput(data: CollectedData): SystemRecInput {
  const inferredWallInsulation = inferWallInsulationFromAgeAndUpgrade(data);

  // Convert UI data to SystemRecInput format
  const compareOptions = data.selectedCompareOptions?.length
    ? data.selectedCompareOptions.map((option, index) => ({
      id: String.fromCharCode(65 + index),
      boiler: option.boiler,
      water: option.water,
    }))
    : undefined;

  // Normalize optional fields before checking
  const normalizedGcNumber = normalizeTextField(data.gcNumber);
  const normalizedBoilerMake = normalizeTextField(data.boilerMake);
  const normalizedBoilerModel = normalizeTextField(data.boilerModel);
  const normalizedSedbukBand = normalizeUnknown(data.sedbukBand);
  const normalizedEpcBand = normalizeUnknown(data.epcBand);
  const normalizedCurrentBoilerKw = (() => {
    const value = (data.currentBoilerKw || '').trim().toLowerCase();
    if (!value || value === 'unknown') return Number.NaN;
    if (value === '<8kw') return 8;
    const parsed = safeParseNumber(value.replace('kw', ''));
    return parsed ?? Number.NaN;
  })();

  return {
    propertyType: data.propertyType,
    propertyAgeBand: data.propertyAgeBand as any,
    bedrooms: safeParseInt(data.bedrooms) ?? 0,
    bathrooms: safeParseInt(data.bathrooms) ?? 0,
    occupancy: safeParseInt(data.occupancyCount) ?? 1,
    occupancyTiming: data.occupancyTiming as any,

    // Water demand
    hotWaterDemand: data.hotWaterDemand as 'low' | 'medium' | 'high',

    // Current system (legacy fields)
    currentBoiler: data.boilerType,
    currentWater: data.waterSystem,
    
    // Explicit system architecture fields (optional in legacy, inferred when possible)
    heatSource: data.currentFuelType as SystemRecInput['heatSource'] || 'unknown',
    dhwArchitecture: data.boilerType === 'combi' || data.boilerType === 'gas_combi' ? 'combi-on-demand' : 'stored-dhw',
    chCircuitType: data.systemType as SystemRecInput['chCircuitType'] || 'unknown',
    controlTopology: undefined,

    // Electrical and pressure
    electricalCapacityConfidence: data.electricalCapacity === 'unknown' ? 'unknown' :
                                   data.electricalCapacity === 'limited' ? 'insufficient' : 'likely_ok',
    mainsPressure: data.mainsPressure === 'not_tested' ? undefined :
                   data.mainsPressure === 'low' ? 1.0 :
                   data.mainsPressure === 'high' ? 4.0 : 2.5,
    flowRate: safeParseNumber(data.flowRate),
    drawsPerDay: safeParseInt(data.drawsPerDay),
    mainsFlowLpm: safeParseNumber(data.mainsFlowLpm),
    mainsStaticPressureBar: safeParseNumber(data.mainsStaticPressureBar),
    mainsDynamicFlowLpm: safeParseNumber(data.mainsDynamicFlowLpm),
    mainsDynamicPressureBar: safeParseNumber(data.mainsDynamicPressureBar),
    coldMainType: data.coldMainType || undefined,

    // Plans
    expectedLengthOfStay: data.lengthOfStay as 'short' | 'medium' | 'long' | 'permanent',
    futureDemand: data.futureDemand as any,
    temporaryGuests: data.temporaryGuests as any,

    // Cost priorities
    costPriority: data.budgetPriority as 'upfront_cost' | 'balanced' | 'ongoing_cost',

    // Upgrade priorities
    upgradePriorities: data.upgradePriorities?.map(p => ({
      key: p.key as any,
      rank: p.rank
    })) || [],

    // Future plans
    futurePlans: normalizeFuturePlans(data).map(plan => ({
      key: plan.key as any,
      horizon: plan.horizon
    })),

    // Disruption
    disruptionTolerance: data.disruptionTolerance as any,

    // Urgency
    urgency: data.urgency as any,

    // Baseline
    baselineEnabled: (() => {
      const hasGC = normalizedGcNumber !== null;
      const hasSedbukBand = normalizedSedbukBand && isValidSedbukBand(normalizedSedbukBand);
      const hasEpcBand = !!normalizedEpcBand;
      const hasAnyBaselineSignal = hasGC || hasSedbukBand || hasEpcBand;
      if (hasAnyBaselineSignal) {
        return true;
      }
      return data.systemAge !== 'unknown' || data.currentFuelType !== '';
    })(),
    existingSystemAge: data.systemAge !== 'unknown' ?
      (data.systemAge === '0-5' ? 3 :
       data.systemAge === '5-10' ? 7 :
       data.systemAge === '10-15' ? 12 :
       data.systemAge === '15-20' ? 17 :
       data.systemAge === '20+' ? 22 : undefined) : undefined,
    currentFuelType: data.currentFuelType as 'gas' | 'oil' | 'electric' | 'lpg',
    currentSystemEfficiency: (() => {
      const hasValidBand = normalizedSedbukBand && isValidSedbukBand(normalizedSedbukBand);
      const hasGC = normalizedGcNumber !== null;
      const hasEpcBand = !!normalizedEpcBand;

      let source: 'SEDBUK' | 'EPC_BAND' | 'USER' | 'ASSUMED' = 'ASSUMED';
      if (hasGC || (hasValidBand && data.efficiencySource === 'sedbuk')) {
        source = 'SEDBUK';
      } else if (hasValidBand) {
        source = 'SEDBUK';
      } else if (hasEpcBand) {
        source = 'EPC_BAND';
      } else if (data.efficiencySource === 'epc') {
        source = 'EPC_BAND';
      }

      return {
        source,
        ...(hasValidBand ? {
          sedbukBand: normalizedSedbukBand as SedbukBand,
          sedbukSeasonal: getEfficiencyFromBand(normalizedSedbukBand as SedbukBand)
        } : {}),
        sedbukRating: data.sedbukRating !== 'unknown' ? parseFloat(data.sedbukRating) : undefined,
        epcBand: normalizedEpcBand as any,
        gcNumber: normalizedGcNumber,
        boilerMake: normalizedBoilerMake,
        boilerModel: normalizedBoilerModel,
        boilerOutputKw: Number.isFinite(normalizedCurrentBoilerKw) ? normalizedCurrentBoilerKw : undefined,
        confidence: hasGC ? 'HIGH' : hasValidBand ? 'MEDIUM' : 'LOW',
      };
    })(),
    sludgeRisk: (() => {
      const risk = (data.sludgeRisk || 'none').toLowerCase();
      if (risk === 'confirmed') return 'CONFIRMED';
      if (risk === 'likely') return 'LIKELY';
      if (risk === 'possible' || risk === 'suspected') return 'POSSIBLE';
      return 'NONE';
    })(),

    // Contamination symptoms
    ...(data.contaminationAssessment === 'assess' ? {
      contaminationSymptoms: {
        radiator_cold_bottom: data.radiatorColdBottom === 'yes',
        kettling_or_whistling: data.kettlingWhistling === 'yes',
        gurgling_air_or_gas: data.gurglingAir === 'yes',
        pump_noise_or_vibration: data.pumpNoise === 'yes',
        frequent_bleeding: data.frequentBleeding === 'yes',
        slow_warmup: data.slowWarmup === 'yes',
        visible_magnetite_on_drain: data.visibleMagnetite === 'yes',
      },
      contaminationSystemFactors: {
        system_type: data.systemType === 'sealed' || data.systemType === 'open_vented' ? data.systemType : undefined,
        has_magnetic_filter: data.hasMagneticFilter === 'yes' ? true : data.hasMagneticFilter === 'no' ? false : undefined,
        hard_water_area: data.hardWaterArea === 'yes' ? true : data.hardWaterArea === 'no' ? false : null,
        system_age_years: data.systemAge !== 'unknown' ?
          (data.systemAge === '0-5' ? 3 :
           data.systemAge === '5-10' ? 7 :
           data.systemAge === '10-15' ? 12 :
           data.systemAge === '15-20' ? 17 :
           data.systemAge === '20+' ? 22 : null) : null,
      },
    } : {}),

    // Emitter profile
    ...(data.emitterType ? {
      emitters: {
        type: data.emitterType as 'radiators' | 'underfloor' | 'mixed' | 'unknown',
        lowTempSuitability: (() => {
          if (data.emitterLowTempSuitability && data.emitterLowTempSuitability !== 'unknown') {
            return data.emitterLowTempSuitability;
          }
          if (data.emitterType === 'radiators' || data.emitterType === 'mixed') {
            return 'definitely_needs_upgrade';
          }
          if (data.emitterType === 'underfloor') {
            return 'likely_ok';
          }
          return 'unknown';
        })() as 'unknown' | 'likely_ok' | 'likely_needs_upgrade' | 'definitely_needs_upgrade',
        roomsStruggleToHeat: (data.emitterRoomsStruggle || 'unknown') as 'yes' | 'no' | 'unknown',
        pipeBore: (data.pipeBore || 'unknown') as '8mm' | '10mm' | '15mm' | '22mm' | 'unknown',
      },
    } : {}),

    // Heat emitter type
    heatEmitterType: (() => {
      if (data.emitterType === 'underfloor') return 'underfloor_heating' as const;
      if (data.emitterType === 'mixed' && data.ufhExtent === 'full') return 'underfloor_heating' as const;
      
      if (RADIATOR_SIZE_MAPPING?.normalize && data.radiatorSize) {
        const normalized = RADIATOR_SIZE_MAPPING.normalize({ radiatorSize: data.radiatorSize });
        if (normalized.heatEmitterType) {
          return normalized.heatEmitterType as 'standard_rads' | 'oversized_rads' | 'underfloor_heating';
        }
      }
      
      return 'standard_rads' as const;
    })(),

    // Insulation level
    insulationLevel: (() => {
      const loft = data.retrofitLoftInsulation;
      const wall = data.retrofitWallInsulation;
      const windows = data.retrofitWindows;
      const draught = data.retrofitDraughtProofing;

      if (!loft && !wall && !windows && !draught) return 'unknown' as const;

      const scoreComponent = (value: string | undefined, isWall = false): number => {
        if (!value || value === 'unknown' || value === 'not_sure') return 1;
        if (value === 'none') return 0;
        if (isWall) {
          if (value === 'solid_uninsulated') return 0;
          if (value === 'cavity' || value === 'solid_insulated') return 2;
        }
        if (value === 'modern' || value === 'good' || value === 'triple') return 2;
        if (value === 'basic' || value === 'partial' || value === 'double' || value === 'older_double') return 1;
        if (value === 'single') return 0;
        return 1;
      };

      const loftScore = scoreComponent(loft);
      const wallScore = scoreComponent(wall, true);
      const windowScore = scoreComponent(windows);
      const draughtScore = scoreComponent(draught);

      const avgScore = (loftScore + wallScore + windowScore + draughtScore) / 4;
      if (avgScore < 0.75) return 'poor' as const;
      if (avgScore < 1.5) return 'medium' as const;
      return 'high' as const;
    })(),

    // Retrofit profile
    ...(data.insulationLevel || data.retrofitLoftInsulation || data.retrofitWallInsulation || data.retrofitWindows || data.retrofitDraughtProofing ? {
      retrofit: {
        loftInsulation: mapRetrofitSelectionsToEngine(
          data.retrofitLoftInsulation || data.insulationLevel,
          'loft'
        ) as any,
        wallInsulation: mapRetrofitSelectionsToEngine(
          data.retrofitWallInsulation || inferredWallInsulation || data.insulationLevel,
          'wall'
        ) as any,
        windows: mapRetrofitSelectionsToEngine(
          data.retrofitWindows || data.insulationLevel,
          'windows'
        ) as any,
        draughtProofing: mapRetrofitSelectionsToEngine(
          data.retrofitDraughtProofing || data.insulationLevel,
          'draught'
        ) as any,
      },
    } : {}),

    // Defaults
    gasAvailable: true,
    spaceAvailability: data.availableSpace === 'limited' || data.availableSpace === 'none' ? 'small' : 'medium',
    numRecommendations: 10,
    compareOptions,

    // Experience questions
    ...(data.simultaneousHotWaterUse || data.heatingContinuityImportance || data.airingCupboardUse ||
        data.spacePressure || data.operatingStylePreference || data.noiseSensitivity ||
        data.environmentalIntent ? {
      experienceQuestions: {
        simultaneousHotWater: data.simultaneousHotWaterUse as 'never' | 'occasionally' | 'most_days' | undefined,
        heatingDuringHotWater: data.heatingContinuityImportance as 'not_important' | 'important' | 'very_important' | undefined,
        airingCupboard: data.airingCupboardUse as 'no' | 'occasionally' | 'yes_regularly' | undefined,
        spacePressure: data.spacePressure as 'very_tight' | 'some_flexibility' | 'plenty' | undefined,
        operatingStyle: data.operatingStylePreference as 'steady_consistent' | 'quick_response' | 'balanced' | undefined,
        noiseSensitivity: data.noiseSensitivity as 'very_sensitive' | 'somewhat' | 'not_bothered' | undefined,
        environmentalIntent: data.environmentalIntent as 'greenest_now' | 'ready_for_later' | 'nice_bonus' | undefined,
      },
    } : {}),

    // Solar intent
    solarIntent: data.solarIntent ? data.solarIntent as 'installed' | 'planned' | 'none' | 'unknown' : undefined,

    // Thermostat preferences
    ...(data.includeThermostat === 'yes' ? {
      thermostatPreference: {
        includeThermostat: true,
        preferredControlType: data.wantLoadCompensation === 'yes' ? 'smart_load_comp' as const :
                             data.wantLoadCompensation === 'no' ? 'smart_on_off' as const : undefined,
        requiredFeatures: {
          loadCompensation: data.wantLoadCompensation === 'yes',
          smartStart: data.wantSmartStart === 'yes',
          multiZone: data.wantMultiZone === 'yes',
        },
        budgetMax: data.thermostatBudget === 'basic' ? 100 :
                   data.thermostatBudget === 'mid' ? 250 :
                   data.thermostatBudget === 'premium' ? 600 : undefined,
        existingThermostatType: (data.currentThermostatType || 'mechanical') as 'mechanical' | 'basic_digital' | 'smart_on_off' | 'smart_load_comp',
      },
    } : {}),

    // Current thermostat type
    currentThermostatType: (data.currentThermostatType || 'mechanical') as 'mechanical' | 'basic_digital' | 'smart_on_off' | 'smart_load_comp',

    // LCOO data
    ...(data.enableLcoo === 'yes' ? {
      lcoo: {
        periodYears: parseInt(data.lcooPeriodYears || '20'),
        tariffs: {
          gasPPerKwh: parseFloat(data.lcooGasTariff || '5.93'),
          elecPPerKwh: parseFloat(data.lcooElecTariff || '27.69'),
        },
        perOption: {
          A: {
            upfrontGrossGBP: parseFloat(data.lcooOptionA_upfront || '0'),
            upfrontIsBeforeGrants: data.lcooOptionA_beforeGrants === 'yes',
            applyBUSGrant: data.lcooOptionA_applyBUS === 'yes',
            annualMaintenanceGBP: parseFloat(data.lcooOptionA_maintenance || '0') || undefined,
            retainExistingCylinder: data.lcooOptionA_retainCylinder === 'yes',
            existingCylinderAgeYears: data.lcooOptionA_retainCylinder === 'yes' ?
              parseFloat(data.lcooOptionA_cylinderAge || '0') || undefined : undefined,
            cylinderCostGBP: data.lcooOptionA_retainCylinder === 'yes' ?
              parseFloat(data.lcooOptionA_cylinderCost || '0') || undefined : undefined,
          },
          B: {
            upfrontGrossGBP: parseFloat(data.lcooOptionB_upfront || '0'),
            upfrontIsBeforeGrants: data.lcooOptionB_beforeGrants === 'yes',
            applyBUSGrant: data.lcooOptionB_applyBUS === 'yes',
            annualMaintenanceGBP: parseFloat(data.lcooOptionB_maintenance || '0') || undefined,
            retainExistingCylinder: data.lcooOptionB_retainCylinder === 'yes',
            existingCylinderAgeYears: data.lcooOptionB_retainCylinder === 'yes' ?
              parseFloat(data.lcooOptionB_cylinderAge || '0') || undefined : undefined,
            cylinderCostGBP: data.lcooOptionB_retainCylinder === 'yes' ?
              parseFloat(data.lcooOptionB_cylinderCost || '0') || undefined : undefined,
          },
          C: {
            upfrontGrossGBP: parseFloat(data.lcooOptionC_upfront || '0'),
            upfrontIsBeforeGrants: data.lcooOptionC_beforeGrants === 'yes',
            applyBUSGrant: data.lcooOptionC_applyBUS === 'yes',
            annualMaintenanceGBP: parseFloat(data.lcooOptionC_maintenance || '0') || undefined,
            retainExistingCylinder: data.lcooOptionC_retainCylinder === 'yes',
            existingCylinderAgeYears: data.lcooOptionC_retainCylinder === 'yes' ?
              parseFloat(data.lcooOptionC_cylinderAge || '0') || undefined : undefined,
            cylinderCostGBP: data.lcooOptionC_retainCylinder === 'yes' ?
              parseFloat(data.lcooOptionC_cylinderCost || '0') || undefined : undefined,
          },
          D: {upfrontGrossGBP: 0, upfrontIsBeforeGrants: false, applyBUSGrant: false},
          E: {upfrontGrossGBP: 0, upfrontIsBeforeGrants: false, applyBUSGrant: false},
        }
      }
    } : {}),
    
    // Dev-only provenance marker for diagnostics
    __source: 'legacy',
  };
}
